/**
 * Inspections request serializer.
 *
 * Converts a normalized ListingQueryState<InspectionFilters> to the URLSearchParams
 * format expected by the existing /inspections/datatable Kendo endpoint.
 *
 * Contract requirements (from the frozen contract §8):
 *  - One URLSearchParams narrowing representation is used for BOTH listing fetches
 *    and every select-all-entirely mass-action request.
 *  - Page/sort parameters may be removed for mass actions; filter/search/asset/scope
 *    values must remain identical.
 *  - search is always included when nonblank (IP-14655 fix).
 *  - asset is sent as a comma-joined string via a separate top-level param.
 *  - Multi-select arrays are order-normalized (sorted) before inclusion.
 *
 * The Kendo endpoint parameters mirror the kendoDatatableParams.js helper output
 * exactly, ensuring backward compatibility without changing the Ruby datatables.
 */
import { normalizeMultiSelect } from '@hooks/listings';
// ---------------------------------------------------------------------------
// Kendo date range adapter
// ---------------------------------------------------------------------------
const KENDO_FIELD_TYPE = 'dateRange';
/** Fallback rangeType for calendar-entered dates when no rangeType is stored. */
const KENDO_CUSTOM_RANGE = 'custom';
/** Converts InspectionDateRange → Kendo date-range sub-parameters.
 *  Emits the stored rangeType (e.g. "next_month") so the backend can apply the
 *  correct date filter. Defaults to 'custom' when rangeType is absent. */
function appendDateRange(params, prefix, range) {
    var _a, _b, _c;
    if (!range.start && !range.end)
        return;
    const rangeType = (_a = range.rangeType) !== null && _a !== void 0 ? _a : KENDO_CUSTOM_RANGE;
    params.append(`${prefix}[rangeType]`, rangeType);
    params.append(`${prefix}[startDate]`, (_b = range.start) !== null && _b !== void 0 ? _b : '');
    params.append(`${prefix}[endDate]`, (_c = range.end) !== null && _c !== void 0 ? _c : '');
    params.append(`${prefix}[fieldType]`, KENDO_FIELD_TYPE);
}
// ---------------------------------------------------------------------------
// Page / sort helpers
// ---------------------------------------------------------------------------
function appendPageParams(params, pageNumber, pageSize) {
    const skip = (pageNumber - 1) * pageSize;
    const take = pageSize;
    params.append('page', String(pageNumber));
    params.append('pageSize', String(take));
    params.append('skip', String(skip));
    params.append('take', String(take));
}
function appendSortParams(params, sort) {
    if (!sort)
        return;
    params.append('sort[0][field]', sort.field);
    params.append('sort[0][dir]', sort.direction);
}
/** Text fields that use the 'contains' operator (mirrors constants.js operators). */
const CONTAINS_FIELDS = new Set(['zipCode', 'referenceNumber', 'workOrder', 'inspectionId']);
function buildFilterEntries(filters) {
    const entries = [];
    const operator = (field) => (CONTAINS_FIELDS.has(field) ? 'contains' : 'eq');
    const addArray = (field, values) => {
        const normalized = normalizeMultiSelect([...values]);
        if (normalized.length === 0)
            return;
        entries.push({ field, operator: operator(field), value: normalized });
    };
    const addText = (field, value) => {
        if (!value)
            return;
        entries.push({ field, operator: operator(field), value });
    };
    const addBoolean = (field, value) => {
        if (value === null)
            return;
        entries.push({ field, operator: 'eq', value: String(value) });
    };
    const addCheckbox = (field, value) => {
        if (!value)
            return;
        entries.push({ field, operator: 'eq', value: 'true' });
    };
    // Multi-selects
    addArray('frequency', filters.frequency);
    addArray('building', filters.building);
    addArray('status', filters.status);
    addArray('technician', filters.technician);
    addArray('technicianTeam', filters.technicianTeam);
    addArray('account', filters.account);
    addArray('territory', filters.territory);
    addArray('additional_territory', filters.additionalTerritory);
    addArray('tags', filters.tags);
    addArray('buildingTags', filters.buildingTags);
    addArray('accountTags', filters.accountTags);
    addArray('workOrderTags', filters.workOrderTags);
    addArray('subcontractor', filters.subcontractor);
    addArray('inspectionType', filters.inspectionType);
    // Scope-injected hidden value
    if (filters.inspectionSeries !== null && filters.inspectionSeries !== undefined) {
        entries.push({ field: 'inspectionSeries', operator: 'eq', value: String(filters.inspectionSeries) });
    }
    // Boolean selects
    addBoolean('buildingStatus', filters.buildingStatus);
    addBoolean('noAccess', filters.noAccess);
    // Text filters
    addText('zipCode', filters.zipCode);
    addText('referenceNumber', filters.referenceNumber);
    addText('workOrder', filters.workOrder);
    addText('inspectionId', filters.inspectionId);
    // Checkbox/integration filters
    addCheckbox('tce', filters.tce);
    addCheckbox('tceErrors', filters.tceErrors);
    addCheckbox('irol', filters.irol);
    addCheckbox('irolErrors', filters.irolErrors);
    addCheckbox('livsafe', filters.livsafe);
    addCheckbox('livsafeErrors', filters.livsafeErrors);
    return entries;
}
/**
 * Injects the listing mount's parent id into filter values.
 *
 * Scope lives on `state.scope` (A1) and is NOT written into persisted
 * `state.filters`. The Kendo `/inspections/datatable` endpoint only
 * understands building / account / inspectionSeries as filter fields, so both
 * the listing fetch and mass-action narrowing must apply the parent here
 * (IP-15555). Standalone mounts are unchanged.
 *
 * Parent id always wins over any leftover filter value for that field.
 */
export function applyScopeToFilters(state) {
    switch (state.scope.type) {
        case 'building':
            return Object.assign(Object.assign({}, state.filters), { building: [state.scope.id] });
        case 'account':
            return Object.assign(Object.assign({}, state.filters), { account: [state.scope.id] });
        case 'inspectionSeries':
            return Object.assign(Object.assign({}, state.filters), { inspectionSeries: state.scope.id });
        default:
            return state.filters;
    }
}
function appendFilterParams(params, filters) {
    const entries = buildFilterEntries(filters);
    // Date ranges (append inline before regular filters to preserve index offset)
    let idx = entries.length;
    if (filters.scheduledDate.start || filters.scheduledDate.end) {
        appendDateRange(params, `filter[filters][${idx}][value]`, filters.scheduledDate);
        params.append(`filter[filters][${idx}][field]`, 'scheduledDate');
        params.append(`filter[filters][${idx}][operator]`, 'eq');
        idx += 1;
    }
    if (filters.inspectionDate.start || filters.inspectionDate.end) {
        appendDateRange(params, `filter[filters][${idx}][value]`, filters.inspectionDate);
        params.append(`filter[filters][${idx}][field]`, 'inspectionDate');
        params.append(`filter[filters][${idx}][operator]`, 'eq');
        idx += 1;
    }
    if (filters.dueDate.start || filters.dueDate.end) {
        appendDateRange(params, `filter[filters][${idx}][value]`, filters.dueDate);
        params.append(`filter[filters][${idx}][field]`, 'dueDate');
        params.append(`filter[filters][${idx}][operator]`, 'eq');
        idx += 1;
    }
    if (filters.visitDate.start || filters.visitDate.end) {
        appendDateRange(params, `filter[filters][${idx}][value]`, filters.visitDate);
        params.append(`filter[filters][${idx}][field]`, 'visitDate');
        params.append(`filter[filters][${idx}][operator]`, 'eq');
        idx += 1;
    }
    if (entries.length === 0 && idx === entries.length)
        return;
    params.append('filter[logic]', 'and');
    entries.forEach((entry, i) => {
        const prefix = `filter[filters][${i}]`;
        params.append(`${prefix}[field]`, entry.field);
        params.append(`${prefix}[operator]`, entry.operator);
        if (Array.isArray(entry.value)) {
            entry.value.forEach((v) => {
                params.append(`${prefix}[value][]`, String(v));
            });
        }
        else {
            params.append(`${prefix}[value]`, String(entry.value));
        }
    });
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Full request serializer: page + sort + filters + search + asset.
 * Used by the React Query fetch function.
 */
export function serializeInspectionsRequest(state) {
    const params = new URLSearchParams();
    const filters = applyScopeToFilters(state);
    appendPageParams(params, state.page.number, state.page.size);
    appendSortParams(params, state.sort);
    appendFilterParams(params, filters);
    if (state.search)
        params.append('search', state.search);
    if (filters.asset.length > 0)
        params.append('asset', normalizeMultiSelect(filters.asset).join(','));
    return params;
}
/**
 * Narrowing-only serializer: filters + search + asset — NO page or sort.
 * Used by ALL mass-action modals to ensure the server resolves the same record
 * set the user saw (IP-14655: omitting search caused over-resolution).
 */
export function serializeInspectionsNarrowing(state) {
    const params = new URLSearchParams();
    const filters = applyScopeToFilters(state);
    appendFilterParams(params, filters);
    if (state.search)
        params.append('search', state.search);
    if (filters.asset.length > 0)
        params.append('asset', normalizeMultiSelect(filters.asset).join(','));
    return params;
}
